import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, getCurrentUser, signOutUser } from '../lib/firebase';
import { createUserProfile, getUserProfile, UserProfile, updateUserProfile as updateProfile, supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: any;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  updateUserProfile: (profile: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  setMockUser: (phoneNumber: string) => void; // For new user flow
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  error: null,
  signOut: async () => {},
  updateUserProfile: async () => {},
  refreshUserProfile: async () => {},
  setMockUser: () => {},
});

// Export the hook separately from its definition
const useAuthHook = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export the hook as a named export
export const useAuth = useAuthHook;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch user profile
  const fetchUserProfile = async (phoneNumber: string) => {
    try {
      setError(null);
      
      // Try to get the user profile from Supabase
      const profile = await getUserProfile(phoneNumber);
      
      if (profile) {
        setUserProfile(profile);
        return profile;
      } else {
        // This shouldn't happen in the new flow since we create profiles upfront
        console.warn('Profile not found for authenticated user:', phoneNumber);
        setUserProfile(null);
        return null;
      }
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      setError(`Failed to load user profile: ${error.message}`);
      return null;
    }
  };

  // Mock user function for new user flow (when no Firebase auth is needed)
  const setMockUser = async (phoneNumber: string) => {
    const mockUser = {
      phoneNumber: phoneNumber,
      uid: phoneNumber, // Use phone number as UID for simplicity
    };
    setUser(mockUser);
    
    // Fetch the profile for this user
    try {
      await fetchUserProfile(phoneNumber);
    } catch (error) {
      console.error('Error fetching profile for new user:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      try {
        setUser(firebaseUser);
        setError(null);
        
        if (firebaseUser && firebaseUser.phoneNumber) {
          await fetchUserProfile(firebaseUser.phoneNumber);
        } else {
          setUserProfile(null);
        }
        
        setLoading(false);
      } catch (error: any) {
        console.error('Error in auth state change handler:', error);
        setError(`Authentication error: ${error.message}`);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      setError(null);
      await signOutUser();
      setUser(null);
      setUserProfile(null);
    } catch (error: any) {
      console.error('Error signing out:', error);
      setError(`Sign out failed: ${error.message}`);
    }
  };

  const updateUserProfile = async (profile: Partial<UserProfile>) => {
    if (!user || !user.phoneNumber) {
      throw new Error('User is not authenticated');
    }
    
    try {
      setError(null);
      const updatedProfile = await updateProfile(user.phoneNumber, profile);
      setUserProfile(updatedProfile);
      return updatedProfile;
    } catch (error: any) {
      console.error('Error updating user profile:', error);
      setError(`Failed to update profile: ${error.message}`);
      throw error;
    }
  };

  // New function to refresh user profile
  const refreshUserProfile = async () => {
    if (!user || !user.phoneNumber) {
      throw new Error('User is not authenticated');
    }

    try {
      setError(null);
      const refreshedProfile = await fetchUserProfile(user.phoneNumber);
      return refreshedProfile;
    } catch (error: any) {
      console.error('Error refreshing user profile:', error);
      setError(`Failed to refresh profile: ${error.message}`);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    signOut,
    updateUserProfile,
    refreshUserProfile,
    setMockUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};